/**
 * DailyIntentPicker — Mascot-led bottom sheet that disambiguates the two
 * daily-hub intents: log Today's Checklist or log Together Time. Mirrors
 * CreateIntentPicker so users learn one consistent pattern.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CheckSquare, Users, ChevronRight, RotateCcw } from "lucide-react";
import { useLocalStorage } from "@/hooks/useLocalStorage";

export type DailyIntent = "tasks" | "together";

interface DailyIntentPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (intent: DailyIntent) => void;
  tasksBadge?: string;
  togetherBadge?: string;
}

const options: Array<{
  intent: DailyIntent;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  example: string;
  gradient: string;
}> = [
  {
    intent: "tasks",
    icon: CheckSquare,
    title: "To-Do List",
    description: "Quick wins for today",
    example: "e.g. drink water, 20-min walk",
    gradient: "from-teal-500 to-emerald-500",
  },
  {
    intent: "together",
    icon: Users,
    title: "Family & Friends Check-in",
    description: "Stay close to the people who matter",
    example: "e.g. game night, shared meal",
    gradient: "from-rose-500 to-pink-500",
  },
];

export function DailyIntentPicker({
  open,
  onOpenChange,
  onPick,
  tasksBadge,
  togetherBadge,
}: DailyIntentPickerProps) {
  const { value: lastIntent, setValue: setLastIntent } = useLocalStorage<DailyIntent | null>(
    "daily-intent:last",
    null
  );

  const handlePick = (intent: DailyIntent) => {
    setLastIntent(intent);
    onPick(intent);
    onOpenChange(false);
  };

  const lastOption = options.find((o) => o.intent === lastIntent);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[90vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="text-2xl flex items-center gap-2">
            <span aria-hidden>👋</span> What would you like to log?
          </SheetTitle>
          <SheetDescription>
            Pick one — we'll open the right space for you.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-3 mt-4 pb-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
          {lastOption && (
            <button
              type="button"
              onClick={() => handlePick(lastOption.intent)}
              className="group flex items-center gap-4 rounded-2xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 transition-all p-4 text-left shadow-sm active:scale-[0.99]"
              style={{ touchAction: "manipulation" }}
            >
              <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">Repeat last: {lastOption.title}</p>
                <p className="text-sm text-muted-foreground">One tap to jump back in</p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
            </button>
          )}

          {options.map(({ intent, icon: Icon, title, description, example, gradient }) => {
            const badge = intent === "tasks" ? tasksBadge : togetherBadge;
            return (
              <button
                key={intent}
                type="button"
                onClick={() => handlePick(intent)}
                className="group flex items-center gap-4 rounded-2xl border bg-card hover:bg-accent transition-all p-4 text-left shadow-sm hover:shadow-md active:scale-[0.99]"
                style={{ touchAction: "manipulation" }}
              >
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground">{title}</p>
                    {badge && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-foreground/80 font-medium">
                        {badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{description}</p>
                  <p className="text-xs text-muted-foreground/80 mt-0.5">{example}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
